/**
 * ── Font catalog (additive layer) ─────────────────────────────────────────────
 *
 * The brand font (Nunito) is bundled and self-contained (see `brand-font.ts` /
 * `brand-font.css`). This catalog is the OPTIONAL, purely ADDITIVE layer: a small
 * curated set of typefaces a user may switch to. Picking one sets the
 * `--font-user` override at runtime (see `typography-provider`); Reset clears it
 * and returns to the bundled brand — always available with ZERO catalog/network
 * dependency.
 *
 * Privacy-first ordering: the entries that need NO network (system faces) come
 * first so the picker is usable and deterministic offline. A couple of Fontsource
 * families are offered as an on-demand download (a `<link>` is appended only when
 * the user actually selects them) — never at boot, and never for the brand.
 *
 * NOTE: this is the single source of the selectable catalog for the web app. The
 * dormant `packages/shared/src/typography/preloaded-fonts.json` (template residue,
 * imported by nothing) is not wired in Susurro; this curated list supersedes it
 * without pulling in the PowerSync/registry machinery the accountless app lacks.
 */

export interface CatalogFont {
  /** Stable id (used as the per-account cache id and de-dupe key for the loader). */
  id: string;
  /** Display + CSS family name (applied as the `--font-user` override). */
  name: string;
  /** Rough classification, shown in the picker. */
  category: "sans-serif" | "serif";
  /**
   * Optional stylesheet to lazily fetch when (and only when) the user selects this
   * font. System faces omit it (already present → no network). The brand NEVER
   * appears here.
   */
  stylesheetUrl?: string;
}

export const FONT_CATALOG: readonly CatalogFont[] = [
  // System faces — no download, work offline, privacy-preserving.
  { id: "georgia", name: "Georgia", category: "serif" },
  { id: "system-sans", name: "Verdana", category: "sans-serif" },
  // Fontsource web fonts — fetched on demand only when picked.
  {
    id: "inter",
    name: "Inter",
    category: "sans-serif",
    stylesheetUrl: "https://cdn.jsdelivr.net/npm/@fontsource/inter@5/index.css",
  },
  {
    id: "lora",
    name: "Lora",
    category: "serif",
    stylesheetUrl: "https://cdn.jsdelivr.net/npm/@fontsource/lora@5/index.css",
  },
] as const;

/** Look up a catalog entry by id (undefined for the brand / an unknown cached id). */
export function findCatalogFont(id: string | null | undefined): CatalogFont | undefined {
  if (!id) {
    return undefined;
  }
  return FONT_CATALOG.find((f) => f.id === id);
}
